import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import PageHeader from "components/common/PageHeader";
import TableShimmer from "components/common/TableShimmer";
import DataTable from "components/common/table/DataTable";
import Switch from "components/common/Switch";
import { StatusBadge } from "components/common/StatusBadge";
import { handleStatusUpdate } from "helpers/apiUtils";
import { showDeleteModal } from "store/slices/modalSlice";
import { usePermission } from "hooks/usePermission";
import { getActionFlags } from "utils/getActionFlags";
import { getMinMaxDate, formatUTCDate } from "utils/utils";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import {
  getQrLinks,
  resetFilters,
  setFilter,
  updateQrLinkStatus,
} from "store/slices/qrLinksSlice";

const QRLinks = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { view, edit } = Icons || {};
  const { canAction } = usePermission();
  const { canView, canEdit, canAdd, canDelete } = canAction("qr_links");

  
  const {
    loading,
    isLoading,
    loadingItemId,
    items,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, locationName, dates, status, orderBy },
  } = useSelector((state) => state.QRLinks);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    dispatch(
      getQrLinks({
        pageNo: page || 1,
        perPage: 10,
        orderBy: isReset ? -1 : orderBy,
        status: isReset ? -1 : status,
        keyword: isResetKeyword ? "" : keyword || "",
        locationName: isReset ? "" : locationName || "",
        dateRange: isReset ? {} : getMinMaxDate(dates),
      }),
    );
  };

  useEffect(() => {
    if (canView) getAllData();
  }, [canView]);

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filters = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      { header: t("Label"), key: "label" },
      { header: t("QR Identifier"), key: "identifierCode" },
      {
        header: t("Location"),
        key: "dynamicParams",
        render: (value) => value?.locationName || "-",
      },
      { header: t("Fallback URL"), key: "fallbackUrl" },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => formatUTCDate(value),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  if (!canView) return null;

  return (
    <div className="page-content">
      <PageHeader title={t("QR Links")} />
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search QR links")}
        filters={filters}
        selectedFilters={{ locationName, status, orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add QR Link")}
        onBtnClick={() => navigate("/qr-links/add")}
      />

      {loading && <TableShimmer rows={10} columns={6} />}
      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No QR links found")} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            onRowClick={(item) => navigate(`/qr-links/details?id=${item._id}`)}
            renderActions={(item) => (
              <>
                {canEdit && (
                  <Switch
                    checked={item.status === 1}
                    handleOnChange={() =>
                      handleStatusUpdate({
                        _id: item._id,
                        status: item.status,
                        dispatch,
                        action: updateQrLinkStatus,
                      })
                    }
                    disabled={isLoading && loadingItemId === item._id}
                  />
                )}
                {canEdit && (
                  <img
                    src={edit}
                    width={20}
                    height={20}
                    alt={t("edit")}
                    onClick={() => navigate(`/qr-links/edit?id=${item._id}`)}
                  />
                )}
                {canDelete && (
                  <img
                    src={Icons.trash}
                    width={20}
                    height={20}
                    alt={t("delete")}
                    onClick={() =>
                      dispatch(
                        showDeleteModal({
                          title: t("Delete QR Link"),
                          message: t(
                            "Are you sure you want to delete this QR Link?",
                          ),
                          itemId: item._id,
                          actionType: "QR_LINK",
                        }),
                      )
                    }
                  />
                )}
              </>
            )}
            t={t}
          />
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default QRLinks;
